import { useMemo, useRef } from 'react'
import {
  compareIsoDate,
  eventColorVar,
  HOURS,
  HOUR_ROW_HEIGHT,
  parseTimeMinutes,
  toIsoDate,
  type CalendarEvent,
} from '../calendarHelpers'
import { Pressable } from '../../Common/Pressable'

interface DayViewProps {
  focusedDate: Date
  allDayEvents: CalendarEvent[]
  timedEvents: CalendarEvent[]
  onEventClick: (event: CalendarEvent) => void
  onCellClick: (date: Date) => void
  onEventDragStart: (event: CalendarEvent, dateIso: string, minutes?: number) => void
  onEventDragOver: (dateIso: string, minutes?: number) => void
  onEventDragEnd: () => void
  draggingEventId?: string | null
}

const minutesToPercent = (minutes: number) => (minutes / (24 * 60)) * 100

export const DayView = ({
  focusedDate,
  allDayEvents,
  timedEvents,
  onEventClick,
  onCellClick,
  onEventDragStart,
  onEventDragOver,
  onEventDragEnd,
  draggingEventId,
}: DayViewProps) => {
  const iso = useMemo(() => toIsoDate(focusedDate), [focusedDate])
  const colRef = useRef<HTMLDivElement | null>(null)

  const dayAllDay = useMemo(
    () => allDayEvents.filter((event) => compareIsoDate(event.startDate, iso) <= 0 && compareIsoDate(event.endDate, iso) >= 0),
    [allDayEvents, iso],
  )
  const dayTimed = useMemo(
    () => timedEvents.filter((event) => event.startDate === iso),
    [timedEvents, iso],
  )

  const getMinutesFromY = (clientY: number): number | undefined => {
    const node = colRef.current
    if (!node) return undefined
    const bounds = node.getBoundingClientRect()
    const ratio = (clientY - bounds.top) / bounds.height
    return Math.max(0, Math.min(24 * 60 - 15, Math.round((ratio * 24 * 60) / 15) * 15))
  }

  return (
    <div className="calDay" data-cal-view="day" onPointerUp={onEventDragEnd}>
      <header className="calDayHead">
        <h3>{focusedDate.toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
      </header>

      {dayAllDay.length > 0 && (
        <section className="calDayAllDay" aria-label="All-day events">
          <span className="calDayAllDayLabel">all-day</span>
          <div className="calDayAllDayChips">
            {dayAllDay.map((event) => (
              <Pressable
                key={event.id}
                className={`calDayAllDayChip calDayAllDayChip--${event.kind}${event.isCancelled ? ' isCancelled' : ''}`}
                style={{
                  ['--cal-accent' as string]: eventColorVar(event.kind),
                } as React.CSSProperties}
                onClick={() => onEventClick(event)}
                onPointerDown={(pointerEvent) => {
                  pointerEvent.stopPropagation()
                  onEventDragStart(event, event.startDate)
                }}
              >
                {event.title}
              </Pressable>
            ))}
          </div>
        </section>
      )}

      <div className="calDayBody">
        <div className="calDayHours">
          {HOURS.map((hour) => (
            <div key={hour} className="calDayHourRow" style={{ height: HOUR_ROW_HEIGHT }}>
              <span>{`${hour.toString().padStart(2, '0')}:00`}</span>
            </div>
          ))}
        </div>
        <div
          className="calDayColumn"
          ref={colRef}
          onPointerMove={(pointerEvent) => {
            if (!draggingEventId) return
            const minutes = getMinutesFromY(pointerEvent.clientY)
            onEventDragOver(iso, minutes)
          }}
          onClick={(pointerEvent) => {
            if ((pointerEvent.target as HTMLElement).closest('.calDayTimedEvent')) return
            onCellClick(focusedDate)
          }}
        >
          {HOURS.map((hour) => (
            <div key={hour} className="calDayHourSlot" style={{ height: HOUR_ROW_HEIGHT }} />
          ))}
          {dayTimed.map((event) => {
            const startMin = parseTimeMinutes(event.startTime) ?? 0
            const endMin = parseTimeMinutes(event.endTime) ?? startMin + 60
            const isDragging = event.id === draggingEventId
            return (
              <Pressable
                key={event.id}
                className={`calDayTimedEvent calDayTimedEvent--${event.kind}${isDragging ? ' isDragging' : ''}${event.isCancelled ? ' isCancelled' : ''}`}
                style={{
                  top: `${minutesToPercent(startMin)}%`,
                  height: `${minutesToPercent(endMin - startMin)}%`,
                  ['--cal-accent' as string]: eventColorVar(event.kind),
                } as React.CSSProperties}
                onClick={(pointerEvent) => {
                  pointerEvent.stopPropagation()
                  onEventClick(event)
                }}
                onPointerDown={(pointerEvent) => {
                  pointerEvent.stopPropagation()
                  const minutes = getMinutesFromY(pointerEvent.clientY)
                  onEventDragStart(event, iso, minutes)
                }}
                title={`${event.title} ${event.startTime}–${event.endTime}`}
              >
                <strong>{event.title}</strong>
                <span>{event.startTime} – {event.endTime}</span>
              </Pressable>
            )
          })}
        </div>
      </div>
    </div>
  )
}
